/**
 * Android hardware/gesture back-button handling (CAP-05).
 *
 * Native-only (isRunningInNativeShell() gated) — the web app has no hardware
 * back button and relies on the browser's own history navigation.
 *
 * Precedence, per research Pattern 6:
 * 1. Any open dialog/menu overlay -> dispatch a synthetic Escape keydown.
 *    Radix Dialogs close themselves on Escape (their DismissableLayer listens
 *    on `document`). The two non-Radix overlays (profile dropdown in
 *    routes/_app.tsx, ExportMenu) get an explicit `document`-level keydown
 *    listener added alongside their existing outside-click-close handlers so
 *    this same synthetic Escape reaches them too.
 * 2. Summary drawer open -> close it.
 * 3. Router history has entries AND we're not at the root route -> go back.
 * 4. Otherwise (top of the stack) -> exit the app.
 */
import type { AnyRouter } from '@tanstack/react-router';
import { App } from '@capacitor/app';
import { useCalendarStore } from '@/features/calendar/calendarStore';
import { isRunningInNativeShell } from './pwa';

export function registerNativeBackButton(router: AnyRouter): void {
  if (!isRunningInNativeShell()) return;

  void App.addListener('backButton', () => {
    // 1. Any open dialog OR menu overlay? Dispatch a synthetic Escape.
    if (document.querySelector('[role="dialog"], [role="menu"]')) {
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      return;
    }

    // 2. Summary drawer open? Close it.
    if (useCalendarStore.getState().summaryOpen) {
      useCalendarStore.getState().closeSummary();
      return;
    }

    // 3. Can we go back in history and we're not at the root route? Go back.
    if (router.history.canGoBack() && router.state.location.pathname !== '/') {
      router.history.back();
      return;
    }

    // 4. At the top of the stack — exit the app.
    void App.exitApp();
  });
}
